import { useEffect, useEffectEvent, useLayoutEffect, useRef } from 'react';

type StandardKey =
    | '0'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7'
    | '8'
    | '9'
    | 'a'
    | 'b'
    | 'c'
    | 'd'
    | 'e'
    | 'f'
    | 'g'
    | 'h'
    | 'i'
    | 'j'
    | 'k'
    | 'l'
    | 'm'
    | 'n'
    | 'o'
    | 'p'
    | 'q'
    | 'r'
    | 's'
    | 't'
    | 'u'
    | 'v'
    | 'w'
    | 'x'
    | 'y'
    | 'z'
    | 'F1'
    | 'F2'
    | 'F3'
    | 'F4'
    | 'F5'
    | 'F6'
    | 'F7'
    | 'F8'
    | 'F9'
    | 'F10'
    | 'F11'
    | 'F12'
    | 'ArrowUp'
    | 'ArrowDown'
    | 'ArrowLeft'
    | 'ArrowRight'
    | 'CapsLock'
    | 'NumLock'
    | 'ScrollLock'
    | 'Home'
    | 'End'
    | 'PageUp'
    | 'PageDown'
    | 'Enter'
    | 'Tab'
    | ' '
    | 'Backspace'
    | 'Delete'
    | 'Insert'
    | 'Escape'
    | 'ContextMenu'
    | 'PrintScreen'
    | 'Pause'
    | '.'
    | ','
    | ';'
    | "'"
    | '`'
    | '['
    | ']'
    | '\\'
    | '/'
    | '-'
    | '='
    | '*'
    | '+'
    | '-'
    | '.'
    | '/';

type ModifierKey = 'Shift' | 'Control' | 'Alt' | 'Meta';
type Key = StandardKey | ModifierKey;
type KeyCombo = Key | `${ModifierKey}+${StandardKey}`;

interface Options {
    event?: 'keydown' | 'keyup';
    target?: Window | Document | HTMLElement | null;
    enabled?: boolean;
    repeat?: boolean;
}

export const useKeyPress = (
    keyCombo: KeyCombo | KeyCombo[],
    callback: (event: KeyboardEvent) => void,
    options: Options = {},
) => {
    const { event = 'keydown', target = window, repeat = false, enabled = true } = options;
    const callbackRef = useRef(callback);

    const handleKeyPress = useEffectEvent((e: Event) => {
        const keyboardEvent = e as KeyboardEvent;
        if (!repeat && keyboardEvent.repeat) return;

        const combos = Array.isArray(keyCombo) ? keyCombo : [keyCombo];
        const match = combos.some((combo) => isKeyMatch(keyboardEvent, combo));

        if (!match) return;

        callbackRef.current(keyboardEvent);
    });

    useLayoutEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        if (!target || !enabled) return;

        target.addEventListener(event, handleKeyPress);

        return () => {
            target.removeEventListener(event, handleKeyPress);
        };
    }, [enabled, event, handleKeyPress, target]);
};

const isKeyMatch = (event: KeyboardEvent, combination: KeyCombo) => {
    const keys = combination.split('+') as Key[];

    if (event.key !== keys[keys.length - 1]) return false;
    if (event.ctrlKey !== keys.includes('Control')) return false;
    if (event.shiftKey !== keys.includes('Shift')) return false;
    if (event.altKey !== keys.includes('Alt')) return false;
    if (event.metaKey !== keys.includes('Meta')) return false;

    return true;
};
